/**
 * Prazo de acesso: lista e alteração na ADM, e o prazo do próprio aluno.
 * Contrato em backend/src/access-period (AccessAdminController e
 * AccessStudentController).
 */
import i18n from '../config/i18n';
import { authenticatedFetch } from './authService';

export type AccessSituation =
  | 'ativo'
  | 'vence_em_breve'
  | 'vencido'
  | 'sem_prazo'
  | 'cancelado_reembolsado'
  | 'bloqueado'
  | 'equipe';

/** A partir de quantos dias do fim o aviso ao aluno vira destaque (TEXTO 2). Igual ao backend. */
export const ACCESS_WARNING_DAYS = 30;

export type ExpiryFilter = 'todos' | 'proximos_30' | 'proximos_7' | 'vencidos' | 'sem_prazo';

export interface AccessFilter {
  /** Produto (BR, LATAM). O admin é global: sem produto, todos. */
  productId?: number;
  /** 'YYYY-MM-DD' */
  purchasedFrom?: string;
  purchasedTo?: string;
  expiry?: ExpiryFilter;
  search?: string;
}

export interface AccessStudent {
  id: string;
  name: string;
  email: string;
  productId: number;
  paymentStatus: string;
  role: string;
  isStaff: boolean;
  isBlocked: boolean;
  preceptorEnabled: boolean;
  monthlyQuota: number;
  enteredAt: string;
  enteredOn: string;
  accessEndDate: string | null;
  situation: AccessSituation;
  daysLeft: number | null;
  adjusted: boolean;
  lastChange: {
    at: string;
    by: string | null;
    reasonCode: string;
    reasonLabel: string | null;
    note: string | null;
  } | null;
}

export interface AccessStudentList {
  items: AccessStudent[];
  total: number;
  /** Quantos o "selecionar todos do filtro" alcança (sem a equipe). */
  selectableTotal: number;
  page: number;
  limit: number;
  totalPages: number;
  asOf: string;
  today: string;
  enforced: boolean;
}

export interface AccessReason {
  code: string;
  label: string;
  requiresNote: boolean;
}

export interface AccessHistoryEntry {
  id: string;
  at: string;
  by: string | null;
  reasonCode: string;
  reasonLabel: string | null;
  note: string | null;
  previousEndDate: string | null;
  newEndDate: string | null;
  batchId: string | null;
}

export interface ProductAccessSettings {
  productId: number;
  name: string;
  durationMonths: number | null;
  /** A data que um cadastro de hoje receberia. */
  defaultEndDate: string | null;
}

export interface AccessSettings {
  products: ProductAccessSettings[];
  enforced: boolean;
}

export interface MyAccess {
  accessEndDate: string | null;
  daysLeft: number | null;
  enforced: boolean;
}

export type AccessOperation =
  | { type: 'set'; date: string }
  | { type: 'shift'; sign: 1 | -1; amount: number; unit: 'days' | 'months' };

export type AccessSelection = { ids: string[] } | { filter: AccessFilter };

export interface BulkEndDateRequest {
  selection: AccessSelection;
  operation: AccessOperation;
  reasonCode: string;
  note?: string;
  expectedCount: number;
  asOf: string;
  confirmPast?: boolean;
}

/** Erro da API com o `code` do corpo, quando houver (ex.: CONFIRM_PAST_REQUIRED). */
export class AccessApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'AccessApiError';
  }
}

async function request<T>(endpoint: string, init?: RequestInit): Promise<T> {
  const response = await authenticatedFetch(endpoint, init);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const message = Array.isArray(body.message) ? body.message.join(' ') : body.message;
    throw new AccessApiError(message || i18n.t('errors.generic'), response.status, body.code);
  }
  return response.json() as Promise<T>;
}

/** Só os filtros preenchidos: `search=` vazio não pode virar filtro. */
export function cleanFilter(filter: AccessFilter): AccessFilter {
  const clean: AccessFilter = {};
  if (filter.productId) clean.productId = filter.productId;
  if (filter.purchasedFrom) clean.purchasedFrom = filter.purchasedFrom;
  if (filter.purchasedTo) clean.purchasedTo = filter.purchasedTo;
  if (filter.expiry && filter.expiry !== 'todos') clean.expiry = filter.expiry;
  if (filter.search?.trim()) clean.search = filter.search.trim();
  return clean;
}

export function listAccessStudents(
  filter: AccessFilter,
  page: number,
  limit = 25,
): Promise<AccessStudentList> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  Object.entries(cleanFilter(filter)).forEach(([key, value]) => params.set(key, String(value)));
  return request(`/admin/access/students?${params.toString()}`);
}

export function bulkUpdateEndDate(body: BulkEndDateRequest): Promise<{ updated: number; batchId: string }> {
  return request('/admin/access/students/bulk-end-date', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getAccessHistory(accountId: string): Promise<AccessHistoryEntry[]> {
  return request(`/admin/access/students/${encodeURIComponent(accountId)}/history`);
}

export function getAccessReasons(): Promise<AccessReason[]> {
  return request('/admin/access/reasons');
}

export function getAccessSettings(): Promise<AccessSettings> {
  return request('/admin/access/settings');
}

export function updateAccessSettings(
  productId: number,
  durationMonths: number | null,
): Promise<AccessSettings> {
  return request(`/admin/access/settings/${productId}`, {
    method: 'PATCH',
    body: JSON.stringify({ durationMonths }),
  });
}

export function getMyAccess(): Promise<MyAccess> {
  return request('/access/me');
}
